'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { generateRoomId } from '@/lib/client-utils';
import { segmentClient } from '@/lib/analytics/segment';
import { CustomSignInButton } from './CustomSignInButton';

const CHARACTER_PHRASES = [
  'any character you can imagine ✨',
  'a demon-slayer on an enchanted motorbike',
  'a cursed vampire with irresistible looks',
  'a jewel thief with a heart of gold',
  'a bard whose songs doom listeners to fall in love',
  'a time-traveling assassin seeking revenge',
  'a regal porpoise with a fondness for tea',
  'a shapeshifting spy who forgot their true form',
  'a croissant who just gained sentience one day',
];

// Simple starter suggestions (no names, short) - one randomly populates the text box
const STARTER_SUGGESTIONS = [
  'a fierce tiger warrior in golden armor',
  'a mysterious shadow assassin',
  'a powerful dragon sorceress',
  'a charming rogue with a silver tongue',
  'a towering frost giant queen',
  'a cunning fox spirit',
  'a vengeful fallen angel',
  'a wild berserker with flaming fists',
  'a seductive vampire huntress',
  'a giant armored war cat',
  'a lightning-fast sword dancer',
  'a dark elf witch queen',
];

const LUCKY_CHARACTER_DESCRIPTIONS = [
  'a time-traveling potato wizard named Spudrick, clever but clumsy, wielding a forked lightning staff and wearing a ticking pocket-watch monocle',
  'a gigantic cookie knight called Mookie, all brawn and low magic, swinging an 800-degree oven door as a shield and wearing candy-coated armor',
  'a cursed vampire bard named Shadowfang, charming but fragile, dressed in tattered noble finery and singing with a blood-red crystal mic that drains courage',
  'a magical talking cat called Sotel, wise and mysterious, with glowing green eyes, a rune-carved collar, and spectral claws that extend into phantom blades',
  'a disco octopus dancer named Sir Licks-a-Lot, high on wit and rhythm, juggling rubber chickens of doom beneath a crown of spinning mirror balls',
  'a soda spirit named Lemon-Lime Fizzy, bubbly and persuasive, slashing foes with a seven-shaped sword and crowned by a fizzing halo of carbonation',
  'a dragon-sized housecat called Thunderpaws, all brawn and no subtlety, wearing a tiny golden bell and wielding a telephone pole as a scratching post spear',
  'a kobold artificer named Ren, razor-smart but physically frail, tinkering with a bolt pistol and a backpack of half-finished explosive inventions',
  'a spectral wraith bride named Silas Coffinnail, brilliant yet unsettling, drifting in a moldy yellow wedding dress studded with eerie green gems',
  'a heroic hamster named Turbo, speedy and charismatic, racing inside a translucent battle sphere that can transform into any beast he imagines',
  'a fallen unicorn warrior called Diphin Hydromin, stoic and mighty, wearing cracked holy armor and fighting with a folding sword of shimmering starlight',
  'a hard-boiled wizard detective named Anson Walker, more magic than brawn, in a silver-trimmed trench coat with a dragon-headed cane that fires arcane blasts',
  'a colossal 500-foot titaness named Enhrallduh, nearly pure brawn, stomping battlefields bare-handed and wearing only stormclouds and starlight as adornments',
  'a bardic kraken DJ called Glitterhoof the Unicorn DJ, equal parts wit and chaos, scratching records with tentacles and firing banana nunchucks from her horn',
  'a mechanical juggernaut named Persephone, towering and relentless, in a skintight armored exosuit with a grenade launcher of fifteen bizarre energy flavors',
  'a gentle caretaker werewolf named Fenric, strong but soft-spoken, carrying a battered healer’s satchel and wearing a cloak lined with protective runes',
  'a battle-scarred panther queen named Nyx, fierce and calculating, prowling in obsidian armor with a tail-blade that cuts through shadow itself',
  'a nine-tailed fox sorceress called Lady Emberfang, cunning and magnetic, wrapped in silken battle robes with claws that ignite into ghostfire',
  'a valkyrie warlord named Sigrid Ironwing, towering and unyielding, clad in form-fitting runed plate with a hammer that calls down thunder',
  'a dragon-blooded empress named Seras, regal and devastating, scales glinting beneath war silks with a voice that commands flame',
];

type AnimatedLandingPageProps = {
  /**
   * When the landing page is controlled by an experiment switcher, we track
   * `Screen Displayed` after the variant is resolved there (to avoid misattribution).
   */
  disableScreenDisplayedTracking?: boolean;
};

export function AnimatedLandingPage({ disableScreenDisplayedTracking }: AnimatedLandingPageProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [displayText, setDisplayText] = useState(CHARACTER_PHRASES[0]);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [characterDescription, setCharacterDescription] = useState(
    () => STARTER_SUGGESTIONS[Math.floor(Math.random() * STARTER_SUGGESTIONS.length)],
  );

  // Get referral params
  const referralSource = searchParams.get('ref');
  const referralCharacterId = searchParams.get('character_id');

  // Typing animation effect
  useEffect(() => {
    const targetPhrase = CHARACTER_PHRASES[currentPhraseIndex];
    let timeout: NodeJS.Timeout;

    if (isDeleting) {
      // Deleting phase
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 10); // Fast delete speed
      } else {
        // Done deleting, pause then start typing next phrase
        timeout = setTimeout(() => {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % CHARACTER_PHRASES.length);
        }, 300); // Pause before typing
      }
    } else {
      // Typing phase
      if (displayText.length < targetPhrase.length) {
        timeout = setTimeout(() => {
          setDisplayText(targetPhrase.slice(0, displayText.length + 1));
        }, 40); // Typing speed (faster)
      } else {
        // Done typing, pause then start deleting
        // First phrase stays longer
        const pauseTime = currentPhraseIndex === 0 ? 10000 : 6000;
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, pauseTime);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentPhraseIndex]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530); // Cursor blink speed

    return () => clearInterval(cursorInterval);
  }, []);

  // Track screen displayed
  useEffect(() => {
    if (disableScreenDisplayedTracking) return;
    segmentClient.track('Screen Displayed', {
      eventCategory: 'game setup',
      eventSubCategory: 'landing-page',
      displayChoices: ['Create Character', "I'm feeling lucky", 'Sign In'],
      ...(referralSource && { referralSource }),
      ...(referralCharacterId && { characterId: referralCharacterId }),
    });
  }, [disableScreenDisplayedTracking, referralSource, referralCharacterId]);

  const handleLuckyClick = () => {
    const randomPhrase =
      LUCKY_CHARACTER_DESCRIPTIONS[Math.floor(Math.random() * LUCKY_CHARACTER_DESCRIPTIONS.length)];

    segmentClient.track('Button Pressed', {
      choiceValue: "I'm feeling lucky",
      eventCategory: 'game setup',
      eventSubCategory: 'landing-page',
      displayChoices: ['Create Character', "I'm feeling lucky", 'Sign In'],
      text: randomPhrase,
    });
    setCharacterDescription(randomPhrase);
  };

  const submitCharacterDescription = () => {
    const trimmedDescription = characterDescription.trim();

    // Track the event
    segmentClient.track('Button Pressed', {
      choiceValue: 'Create Character',
      eventCategory: 'game setup',
      eventSubCategory: 'landing-page',
      displayChoices: ['Create Character', "I'm feeling lucky", 'Sign In'],
      text: trimmedDescription,
    });

    // Store description in localStorage
    localStorage.setItem('pendingCharacterDescription', trimmedDescription);

    // Generate room ID and navigate
    const roomId = generateRoomId();
    router.push(`/character_creation_room/${roomId}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitCharacterDescription();
  };

  return (
    <div className="relative flex flex-col items-center justify-center h-dvh overflow-hidden">
      {/* Full-screen background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src="/trailer.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark overlay for better text readability */}
      <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 z-10"></div>

      {/* Logo - top left */}
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-30">
        <div className="relative w-40 xl:w-60 h-12 xl:h-16">
          <Image
            src="/game_ui/wits-end-logo-light.png"
            alt="Wit's End"
            fill
            className="object-contain"
            priority
          />
        </div>
      </div>

      {/* Sign In button - top right */}
      <div className="absolute top-4 right-4 z-30">
        <CustomSignInButton />
      </div>

      {/* Content overlay */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full text-center px-4 w-full max-w-8xl mx-auto">
        {/* Animated Headline - Fixed height container */}
        <div className="h-32 sm:h-40 md:h-48 flex items-center justify-center w-full max-w-4xl md:max-w-6xl mx-auto">
          <h1
            className=" text-white w-full"
            style={{
              fontSize: 'clamp(2.5rem, 8vw, 6rem)',
              lineHeight: '1',
            }}
          >
            <span className="whitespace-nowrap">Become </span>
            {displayText}
            <span
              className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
            >
              |
            </span>
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-xl leading-relaxed mx-auto mt-8">
          Create your hero for Wit's End, a voice-driven AI fantasy adventure.
        </p>

        {/* Character Description Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto mt-10">
          <div className="flex flex-col sm:flex-row items-stretch gap-3 rounded-3xl bg-white/10 backdrop-blur-md border-2 border-white/20 px-5 py-4 sm:px-6 sm:py-5">
            <div className="flex-1">
              <textarea
                rows={3}
                placeholder="Describe your character..."
                className="w-full bg-transparent text-white placeholder:text-white/50 text-base sm:text-lg resize-none focus:outline-none"
                value={characterDescription}
                onChange={(e) => setCharacterDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitCharacterDescription();
                  }
                }}
              />
            </div>
            <div className="flex flex-row sm:flex-col md:flex-row items-end sm:items-center justify-end gap-3 self-end">
              <button
                type="button"
                onClick={handleLuckyClick}
                aria-label="I'm feeling lucky"
                className="flex items-center justify-center h-10 w-10 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/70 bg-white/5 hover:bg-white/10 transition-colors"
              >
                <span className="text-lg">🎲</span>
              </button>
              <button
                type="submit"
                aria-label="Create character"
                className="px-5 sm:px-6 py-2 rounded-full bg-gradient-to-r from-amber-700 via-amber-600 to-amber-500 text-sm sm:text-base font-semibold text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/60 hover:brightness-110 active:scale-95 transition-all"
              >
                Create✨
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
